import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { LuChevronsUpDown } from "react-icons/lu";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Lock, Bell } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useSubscription } from "../../context/subscriptionContext";
import { useTeam } from "../../context/teamContext";
import axiosInstance from '../../services/api/axiosInstance';
import teamService from '../../services/teamService';
import preloadData from '../../utils/preloadService';
import notificationService from '../../services/notificationService';
import NotificationsPopup from '../notifications/NotificationsPopup';
import CreateNewDropdown from '../../../features/analytics/mixpanel/components/CreateNewDropdown';
import { useBoards } from '../../../features/analytics/mixpanel/hooks/useBoards';
import { usePinnedBoards } from '../../../features/analytics/mixpanel/context/PinnedBoardsContext';
import Icon from '../Icon';

const Sidebar = ({ isOpen, onClose, onNavigate, hideMarketing, isCompact, currentPage }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const params = useParams();
  const location = useLocation();
  const { boards: analyticsBoards, loadBoards } = useBoards();
  const { pinnedBoardIds } = usePinnedBoards();
  const isInAnalyticsBoards = location.pathname.includes('/analytics/boards');
  const isInAnalytics = location.pathname.includes('/analytics/');
  const currentBoardIdFromPath = (() => {
    const m = location.pathname.match(/\/analytics\/boards\/([^/]+)/);
    return m ? m[1] : null;
  })();
  const navigate = useNavigate();
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem('selectedTeam') || 'defaultTeam');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isActive: hasActiveSubscription, canAccessPage } = useSubscription();
  const { setSelectedTeam: setSelectedTeamContext } = useTeam();
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  
  // Team selector state
  const [curTeams, setCurTeams] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notificationsPopupOpen, setNotificationsPopupOpen] = useState(false);
  const [notificationsPopupLayout, setNotificationsPopupLayout] = useState(null);
  const dropdownRef = useRef(null);
  const teamsRequestRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const notificationsBellButtonRef = useRef(null);
  const notificationsPopupRef = useRef(null);
  
  // User name for display in settings popup
  const [userName, setUserName] = useState('');
  
  // Overview dropdown state
  const [overviewDropdownOpen, setOverviewDropdownOpen] = useState(false);
  const overviewDropdownRef = useRef(null);
  
  // Activity dropdown state
  const [activityDropdownOpen, setActivityDropdownOpen] = useState(false);
  const activityDropdownRef = useRef(null);
  
  // Settings popup state
  const [settingsPopupOpen, setSettingsPopupOpen] = useState(false);
  const settingsPopupRef = useRef(null);
  
  // Support popup state
  const [supportPopupOpen, setSupportPopupOpen] = useState(false);
  const supportPopupRef = useRef(null);
  
  // Create New dropdown state
  const [createNewDropdownOpen, setCreateNewDropdownOpen] = useState(false);
  const createNewDropdownRef = useRef(null);
  
  // Control sidebar expansion state - now based on collapse button instead of hover
  const showExpanded = !isCollapsed;
  
  // On settings page, primary sidebar collapses by default (controlled by Dashboard); do not force visible
  const forceVisible = false;
  
  // Extract the current page from the URL path
  const path = location.pathname;
  const pathSegments = path.split('/').filter(segment => segment);
  const pathCurrentPage = pathSegments.length > 1 ? pathSegments[1] : pathSegments[0] || 'dashboard';
  
  // Define free pages (that don't need subscription)
  const freePages = ['dashboard', 'settings'];
  
  // Handle collapse/expand toggle
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Team selector functions
  const getPlanDisplayName = (subscription) => {
    if (!subscription || !subscription.plan) {
      return null;
    }
    
    const planName = subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1);
    const hasCQ = subscription.hasCQIntelligence;
    
    if (hasCQ && subscription.plan === 'pro') {
      return `${planName} + CQ Intelligence`;
    }
    
    return planName;
  };

  const getPlanBadgeColor = (subscription) => {
    if (!subscription || subscription.status !== 'active') {
      return 'bg-gray-100 text-[#475569]';
    }
    
    switch (subscription.plan) {
      // Remove old plan cases (basic, offchain) as they've been migrated
      case 'pro':
        return subscription.hasCQIntelligence 
          ? 'bg-purple-100 text-purple-700' 
          : 'bg-green-100 text-green-700';
      case 'enterprise':
        return 'bg-indigo-100 text-indigo-700';
      default:
        return 'bg-gray-100 text-[#475569]';
    }
  };

  const fetchCompleteTeamData = async (teamId) => {
    try {
      return await teamService.fetchTeamData(teamId);
    } catch (error) {
      console.error('Error fetching complete team data:', error);
      return null;
    }
  };

  const fetchSubscriptionData = async (teamId) => {
    try {
      return await teamService.fetchSubscriptionData(teamId);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      return null;
    }
  };

  const getCurrentUserEmail = () => {
    try {
      const userString = localStorage.getItem('User');
      if (!userString) return '';
      const userObj = JSON.parse(userString);
      return userObj.email || '';
    } catch (err) {
      console.error('Error getting user email:', err);
      return '';
    }
  };

  const getCurrentUserId = () => {
    try {
      const userString = localStorage.getItem('User');
      if (!userString) return '';
      const userObj = JSON.parse(userString);
      return userObj.id || userObj._id || '';
    } catch (err) {
      console.error('Error getting user ID:', err);
      return '';
    }
  };

  const handleCreateNewTeam = async () => {
    if (isCreatingTeam) return;
    
    setIsCreatingTeam(true);
    setDropdownOpen(false);
    
    try {
      const userEmail = getCurrentUserEmail();
      const userId = getCurrentUserId();
      
      if (!userEmail) {
        alert('Unable to get user email. Please log in again.');
        setIsCreatingTeam(false);
        return;
      }

      // Generate a temporary team name
      const tempTeamName = `New Project ${Date.now()}`;
      
      // Create team with minimal required data - backend will set onboarding as incomplete
      const payload = {
        teamName: tempTeamName,
        email: userEmail,
        description: 'New project',
        industry: 'other',
        teamSize: '1-5',
        websiteUrl: 'https://example.com', // Required field, will be updated in onboarding
        contactEmail: userEmail,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const response = await axiosInstance.post('/team/createNewTeam', payload);
      
      if (response.data && response.data.newTeam) {
        const newTeam = response.data.newTeam;
        
        // Fetch the complete team data to get onboarding status
        const teamResponse = await axiosInstance.get(`/team/${newTeam._id || newTeam.id}`);
        const fullTeamData = teamResponse.data;

        // Create team data structure with incomplete onboarding
        const teamData = {
          _id: newTeam._id || newTeam.id,
          name: newTeam.name || tempTeamName,
          role: 'admin',
          isOwner: true,
          isCreator: true,
          onboarding: {
            isComplete: false, // Force incomplete to trigger onboarding
            businessName: tempTeamName,
            industry: 'other',
            teamSize: '1-5',
            websiteUrl: '',
            contactEmail: userEmail
          }
        };

        // Update the team's onboarding to be incomplete by updating the team directly
        try {
          // Update team to mark onboarding as incomplete
          const updateResponse = await axiosInstance.put(`/team/${newTeam._id || newTeam.id}`, {
            onboarding: {
              isComplete: false,
              businessName: tempTeamName,
              industry: 'other',
              teamSize: '1-5',
              description: '',
              websiteUrl: '',
              contactEmail: userEmail
            }
          });
          
          // Update teamData with the updated onboarding status
          if (updateResponse.data && updateResponse.data.team && updateResponse.data.team.onboarding) {
            teamData.onboarding = updateResponse.data.team.onboarding;
          }
        } catch (updateError) {
          console.error('Could not update onboarding status:', updateError);
          console.error('Update error response:', updateError.response?.data);
          // Still proceed - we'll set it in localStorage
        }

        // Fetch updated team data after onboarding update
        let finalTeamData = teamData;
        try {
          const refreshedTeamResponse = await axiosInstance.get(`/team/${newTeam._id || newTeam.id}`);
          if (refreshedTeamResponse.data) {
            finalTeamData = {
              ...refreshedTeamResponse.data,
              role: 'admin',
              isOwner: true,
              isCreator: true
            };
          }
        } catch (refreshError) {
        }

        // Ensure onboarding is marked as incomplete
        if (!finalTeamData.onboarding || finalTeamData.onboarding.isComplete !== false) {
          finalTeamData.onboarding = {
            ...finalTeamData.onboarding,
            isComplete: false
          };
        }

        // Set as selected team with incomplete onboarding
        localStorage.setItem('selectedTeam', finalTeamData.name);
        localStorage.setItem('selectedTeamData', JSON.stringify({
          ...finalTeamData,
          _lastFetched: Date.now()
        }));
        
        setSelectedTeam(finalTeamData.name);
        if (setSelectedTeamContext) {
          setSelectedTeamContext(finalTeamData);
        }
        
        // Dispatch team data updated event
        window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
          detail: { teamData: finalTeamData }
        }));

        // Small delay to ensure state is updated before navigation
        setTimeout(() => {
          // Navigate to onboarding
          const encodedSlug = encodeURIComponent(finalTeamData.name);
          navigate(`/${encodedSlug}/onboarding`, { replace: true });
        }, 200);
      } else {
        throw new Error('Team creation failed - no team returned');
      }
    } catch (error) {
      console.error('Error creating new team:', error);
      const errorMsg = error.response?.data?.message || 'Failed to create new team. Please try again.';
      alert(errorMsg);
    } finally {
      setIsCreatingTeam(false);
    }
  };

  const handleTeamSelect = async (teamss) => {
    const previousTeam = localStorage.getItem('selectedTeam');
    if (previousTeam !== teamss.name) {
      setIsRefreshing(true);
      
      try {
        localStorage.setItem('selectedTeam', teamss.name);
        setSelectedTeam(teamss.name);
        
        const teamDataToStore = {
          _id: teamss._id,
          name: teamss.name,
          role: teamss.role || 'user',
          isOwner: teamss.isOwner || false
        };
        
        localStorage.setItem('selectedTeamData', JSON.stringify(teamDataToStore));
        
        // Tell backend which team should be the user's default for authenticated APIs
        try {
          const activeTeamId = teamss._id || teamss.id;
          if (activeTeamId) {
            await axiosInstance.post('/team/user/default-team', {
              teamId: activeTeamId
            });
          }
        } catch (err) {
          console.error('Error setting default team on backend during team switch:', err);
        }
        
        window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
          detail: { teamData: teamDataToStore }
        }));
        
        localStorage.removeItem("selectedWebsite");
        localStorage.removeItem("idy");
        sessionStorage.removeItem("preloadedWebsites");
        sessionStorage.removeItem("preloadedContracts");
        
        const fetchCompleteData = async () => {
          try {
            const [completeTeamData, subscriptionData] = await Promise.all([
              fetchCompleteTeamData(teamss._id),
              fetchSubscriptionData(teamss._id)
            ]);
            
            const enhancedTeamData = {
              ...teamDataToStore,
              ...(completeTeamData || {}),
              subscription: subscriptionData?.subscription || null
            };
            
            localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeamData));
            
            window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
              detail: { teamData: enhancedTeamData }
            }));
          } catch (error) {
            console.error("Error fetching complete team data:", error);
          }
        };
        
        fetchCompleteData();
        
        preloadData(true, teamss.name).catch(error => {
          console.error("Error preloading data:", error);
        });
        
        const currentPath = window.location.pathname;
        const encodedTeam = encodeURIComponent(teamss.name);
        if (currentPath.includes('/settings')) {
          navigate(`/${encodedTeam}/settings`, { replace: true });
        } else if (currentPath.includes('/analytics')) {
          // When on home (reports), any board, or any funnel/insight editor, go to new team's home so list loads for that team
          navigate(`/${encodedTeam}/analytics/reports`, { replace: true });
        } else {
          const currentUrl = new URL(window.location.href);
          const pathSegments = currentUrl.pathname.split('/').filter(Boolean);
          if (pathSegments.length > 1 && pathSegments[0] !== teamss.name) {
            pathSegments[0] = teamss.name;
            const newPath = `/${pathSegments.join('/')}`;
            navigate(newPath, { replace: true });
          } else if (pathSegments.length === 0 || pathSegments[0] !== teamss.name) {
            navigate(`/${encodedTeam}/analytics/reports`, { replace: true });
          }
        }
      } catch (error) {
        console.error("Error during team switch:", error);
        localStorage.setItem('selectedTeam', previousTeam);
        setSelectedTeam(previousTeam);
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setDropdownOpen(false);
  };

  // Open Tawk.to support chat - loads script only on first click, hides completely when closed
  const openTawkSupport = () => {
    if (window.Tawk_API && window.Tawk_API.showWidget && window.Tawk_API.maximize) {
      window.Tawk_API.showWidget();
      window.Tawk_API.maximize();
      return;
    }
    if (document.getElementById('tawk-script')) return; // Already loading
    window.Tawk_API = window.Tawk_API || {};
    // When chat minimizes, hide the widget completely (including floating bubble)
    window.Tawk_API.onChatMinimized = function() {
      if (window.Tawk_API.hideWidget) {
        window.Tawk_API.hideWidget();
      }
    };
    window.Tawk_API.onLoad = function() {
      window.Tawk_API.showWidget();
      window.Tawk_API.maximize();
    };
    const s1 = document.createElement('script');
    s1.id = 'tawk-script';
    s1.async = true;
    s1.src = 'https://embed.tawk.to/697c9c380c73c21c37c52292/1jg7c4fos';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    document.head.appendChild(s1);
  };

  const handleLogout = async (event) => {
    event.preventDefault();
    
    try {
      // Get the access token to use as a fallback authentication method
      const accessToken = localStorage.getItem('accessToken');
      
      // Call the logout API endpoint to invalidate refresh token
      await axiosInstance.post('/auth/logout', {}, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      // Clear all auth-related items from localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('selectedTeam');
      localStorage.removeItem('selectedTeamData');
      localStorage.removeItem('selectedWebsite');
      localStorage.removeItem('User');
      localStorage.removeItem('idy');
      localStorage.removeItem('subscriptionStatus');
      
      // Clear sessionStorage
      sessionStorage.removeItem('subscriptionStatus');
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Even if API call fails, still clear localStorage and sessionStorage, then redirect to be safe
      localStorage.removeItem('accessToken');
      localStorage.removeItem('selectedTeam');
      localStorage.removeItem('selectedTeamData');
      localStorage.removeItem('selectedWebsite');
      localStorage.removeItem('User');
      localStorage.removeItem('idy');
      localStorage.removeItem('subscriptionStatus');
      
      // Clear sessionStorage
      sessionStorage.removeItem('subscriptionStatus');
      
      navigate('/login');
    }
  };
  
  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      const now = Date.now();
      
      // Check sessionStorage cache first
      const cached = sessionStorage.getItem('userTeams');
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 5 minutes old
          if (Date.now() - timestamp < 5 * 60 * 1000) {
            setCurTeams(data);
            lastFetchTimeRef.current = timestamp;
            return;
          }
        } catch (e) {
          // Failed to parse cached teams
        }
      }
      
      if (isLoadingTeams || (now - lastFetchTimeRef.current < 30000)) {
        return;
      }
      
      try {
        setIsLoadingTeams(true);
        lastFetchTimeRef.current = now;
        
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get('/team/details', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const teams = response.data.team;
        setCurTeams(teams);
        
        // Sync selectedTeam name with actual team data if it changed
        const currentSelectedTeamName = localStorage.getItem('selectedTeam');
        const currentTeamData = localStorage.getItem('selectedTeamData');
        if (currentTeamData) {
          try {
            const parsed = JSON.parse(currentTeamData);
            // Find the team in the fetched teams list by ID
            const matchingTeam = teams.find(t => 
              (t._id && parsed._id && t._id.toString() === parsed._id.toString()) ||
              (t.id && parsed.id && t.id.toString() === parsed.id.toString())
            );
            
            // If team name changed, update it
            if (matchingTeam && matchingTeam.name && matchingTeam.name !== currentSelectedTeamName) {
              localStorage.setItem('selectedTeam', matchingTeam.name);
              setSelectedTeam(matchingTeam.name);
            }
          } catch (e) {
            console.error('Error syncing team name:', e);
          }
        }
        
        // Cache the teams data
        sessionStorage.setItem('userTeams', JSON.stringify({
          data: teams,
          timestamp: now
        }));
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setIsLoadingTeams(false);
      }
    };

    fetchTeams();
    
    return () => {
      if (teamsRequestRef.current) {
        clearTimeout(teamsRequestRef.current);
      }
    };
  }, []);

  // Listen for team data updates (e.g., after onboarding completion)
  useEffect(() => {
    const handleTeamDataUpdate = (event) => {
      const updatedTeamData = event.detail?.teamData;
      if (!updatedTeamData) return;

      const currentSelectedTeam = localStorage.getItem('selectedTeam');
      const currentTeamData = localStorage.getItem('selectedTeamData');
      
      // Check if this is the currently selected team by comparing IDs
      let isCurrentTeam = false;
      if (currentTeamData) {
        try {
          const parsed = JSON.parse(currentTeamData);
          isCurrentTeam = parsed._id === updatedTeamData._id || 
                         parsed.id === updatedTeamData.id ||
                         (updatedTeamData._id && parsed._id === updatedTeamData._id) ||
                         (updatedTeamData.id && parsed.id === updatedTeamData.id);
        } catch (e) {
          console.error('Error parsing team data:', e);
        }
      }

      // If team name changed, update the selected team
      const newTeamName = updatedTeamData.name || updatedTeamData.onboarding?.businessName;
      if (isCurrentTeam && newTeamName && newTeamName !== currentSelectedTeam) {
        localStorage.setItem('selectedTeam', newTeamName);
        setSelectedTeam(newTeamName);
        
        // Update selectedTeamData with the new data
        localStorage.setItem('selectedTeamData', JSON.stringify({
          ...updatedTeamData,
          _lastFetched: Date.now()
        }));
        
        // Refresh teams list to get updated names
        sessionStorage.removeItem('userTeams');
      }
    };

    window.addEventListener('teamDataUpdated', handleTeamDataUpdate);
    return () => {
      window.removeEventListener('teamDataUpdated', handleTeamDataUpdate);
    };
  }, []);

  // Fetch teams when dropdown opens - always refresh to get latest teams
  useEffect(() => {
    const fetchTeamsIfNeeded = async () => {
      if (dropdownOpen && !isLoadingTeams) {
        const now = Date.now();
        
        // Always fetch fresh data when dropdown opens (ignore cache and length check)
        // Only throttle if we've fetched very recently (within 2 seconds)
        if (now - lastFetchTimeRef.current > 2000) {
          try {
            setIsLoadingTeams(true);
            lastFetchTimeRef.current = now;
            
            // Clear cache to force fresh fetch
            sessionStorage.removeItem('userTeams');
            
            const token = localStorage.getItem("token");
            const response = await axiosInstance.get('/team/details', {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            const teams = response.data.team;
            
            setCurTeams(teams);
            
            // Cache the teams data
            sessionStorage.setItem('userTeams', JSON.stringify({
              data: teams,
              timestamp: now
            }));
          } catch (error) {
            console.error("Error fetching teams:", error);
          } finally {
            setIsLoadingTeams(false);
          }
        }
      }
    };
    
    fetchTeamsIfNeeded();
  }, [dropdownOpen, isLoadingTeams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      // Only close overview dropdown if we're NOT on an overview page
      if (overviewDropdownRef.current && !overviewDropdownRef.current.contains(event.target)) {
        if (!location.pathname.includes('/overview')) {
          setOverviewDropdownOpen(false);
        }
      }
      // Only close activity dropdown if we're NOT on an activity page
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(event.target)) {
        if (!location.pathname.includes('/activity')) {
          setActivityDropdownOpen(false);
        }
      }
      // Support and Settings popups are in same container - close both when clicking outside
      if (supportPopupRef.current && !supportPopupRef.current.contains(event.target)) {
        setSettingsPopupOpen(false);
        setSupportPopupOpen(false);
      }
      // Notifications popup should close when clicking outside bell + popup.
      if (
        notificationsPopupOpen &&
        notificationsPopupRef.current &&
        notificationsBellButtonRef.current &&
        !notificationsPopupRef.current.contains(event.target) &&
        !notificationsBellButtonRef.current.contains(event.target)
      ) {
        setNotificationsPopupOpen(false);
      }
      if (createNewDropdownRef.current && !createNewDropdownRef.current.contains(event.target)) {
        setCreateNewDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [location.pathname, notificationsPopupOpen]);

  // Close notifications popup on navigation.
  useEffect(() => {
    setNotificationsPopupOpen(false);
  }, [location.pathname]);

  // Compute popup placement so it never overflows off-screen.
  useEffect(() => {
    if (!notificationsPopupOpen) {
      setNotificationsPopupLayout(null);
      return;
    }

    const compute = () => {
      const btn = notificationsBellButtonRef.current;
      if (!btn) return;

      const rect = btn.getBoundingClientRect();
      const gap = 8;
      const desiredWidth = 380;
      const desiredHeight = 520;
      const minWidth = 240;

      // Horizontal rule:
      // - start near the left edge of the screen (with margin)
      // - fixed width (clamped to viewport if needed)
      const leftPx = 16;
      const maxWidth = window.innerWidth - leftPx - 16;
      const widthPx = Math.max(0, Math.min(desiredWidth, maxWidth));

      // Anchor the popup by its bottom to the bell button top.
      // This prevents it from running under the viewport when the bell is near the bottom.
      const bottomPx = Math.max(16, window.innerHeight - rect.top);

      setNotificationsPopupLayout({
        wrapper: {
          position: 'fixed',
          bottom: bottomPx,
          left: leftPx,
          transform: 'none',
          zIndex: 9999,
          pointerEvents: 'auto',
        },
        containerStyle: {
          width: widthPx,
          maxWidth: 'calc(100vw - 32px)',
          minWidth: Math.min(minWidth, widthPx),
          height: desiredHeight,
        },
      });
    };

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [notificationsPopupOpen]);
  
  // Keep dropdowns open when navigating within their sections, and open them when navigating to sections
  useEffect(() => {
    // Handle overview dropdown
    if (location.pathname.includes('/overview')) {
      // Open dropdown if we're in the overview section
      setOverviewDropdownOpen(true);
    } else {
      // Close dropdown if navigating away from overview entirely
      setOverviewDropdownOpen(false);
    }
    
    // Handle activity dropdown
if (location.pathname.includes('/activity')) {
      // Open dropdown if we're in the activity section
      setActivityDropdownOpen(true);
    } else {
      // Close dropdown if navigating away from activity entirely
      setActivityDropdownOpen(false);
    }
    // Create New dropdown doesn't auto-open based on route
  }, [location.pathname]);

  // Fetch user details
  useEffect(() => {
    const storedUser = localStorage.getItem('User');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserName(user.name || user.email || 'User');
      } catch (error) {
        setUserName('User');
      }
    }
  }, []);

  // Notification badge polling
  useEffect(() => {
    let mounted = true;

    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        if (!token) return;
        const result = await notificationService.getUnreadCount();
        if (mounted) {
          setUnreadNotifications(result?.unread || 0);
        }
      } catch (_) {
        // Silent fail for sidebar badge polling
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);
  
  useEffect(() => {
    // Always use the team from localStorage as the source of truth
    const storedTeam = localStorage.getItem('selectedTeam') || 'team1';
    
    // If URL params don't match stored team, update the URL to maintain current page
    if (params.team && params.team !== storedTeam) {
      // Get the current path and replace only the team part
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(Boolean);
      
      if (pathSegments.length > 0) {
        // Replace the team in the URL while keeping the rest of the path
        pathSegments[0] = storedTeam;
        const newPath = `/${pathSegments.join('/')}`;
        window.history.replaceState(null, '', newPath);
      }
    }
    
    setSelectedTeam(storedTeam);
  }, [params.team]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && e.target.closest('aside') === null && window.innerWidth < 768) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);
  
  // Helper function to determine if a menu item is active
  const isActive = (page) => {
    // Handle special cases where pageName doesn't match URL segment
    const pageMapping = {
      'history': 'user-journey'
    };
    
    const mappedPage = pageMapping[page] || page;
    return pathCurrentPage === mappedPage;
  };
  
  // Check if we're in the overview section (any page within it)
  const isInOverviewSection = () => {
    return pathCurrentPage === 'overview';
  };
  
  // Check if overview section button should be highlighted (only when on exact section, not subpages)
  const isOverviewActive = () => {
    // Only active if we're on /team/overview (exactly 2 segments), not on subpages
    return pathCurrentPage === 'overview' && pathSegments.length === 2;
  };
  
  // Check if we're in the activity section (any page within it)
  const isInActivitySection = () => {
    return pathCurrentPage === 'activity';
  };
  
  // Check if activity section button should be highlighted (only when on exact section, not subpages)
  const isActivityActive = () => {
    // Only active if we're on /team/activity (exactly 2 segments), not on subpages
    return pathCurrentPage === 'activity' && pathSegments.length === 2;
  };
  
  // Reports nav: active when on reports page
  const isReportsActive = () => location.pathname.includes('/analytics/reports');
  
  // Toggle Create New dropdown
  const toggleCreateNewDropdown = () => {
    setCreateNewDropdownOpen(!createNewDropdownOpen);
  };

  // Only boards the user has pinned; show in PINNED section (visible on all pages).
  const pinnedBoards = analyticsBoards.length > 0 && pinnedBoardIds.length > 0
    ? pinnedBoardIds
        .map((id) => analyticsBoards.find((b) => String(b.id) === id))
        .filter(Boolean)
        .map((b) => ({ id: String(b.id), name: b.name || "Untitled" }))
    : [];
  
  // Toggle overview dropdown and navigate to hub if not on overview page
  const toggleOverviewDropdown = () => {
    const wasOpen = overviewDropdownOpen;
    if (!wasOpen && !isInOverviewSection()) {
      navigate(`/${selectedTeam}/overview/hub`);
      // Open dropdown after navigation (it will stay open due to the route change effect logic)
      setOverviewDropdownOpen(true);
    } else if (!isInOverviewSection()) {
      // Only allow toggling closed if we're NOT on an overview page
      setOverviewDropdownOpen(!wasOpen);
    } else {
      // If we're on an overview page, allow toggling to reopen if it's closed
      setOverviewDropdownOpen(!wasOpen);
    }
  };
  
  // Toggle activity dropdown and navigate to offchain if not on activity page
  const toggleActivityDropdown = () => {
    const wasOpen = activityDropdownOpen;
    if (!wasOpen && !isInActivitySection()) {
      navigate(`/${selectedTeam}/activity/offchain`);
      // Open dropdown after navigation (it will stay open due to the route change effect logic)
      setActivityDropdownOpen(true);
    } else if (!isInActivitySection()) {
      // Only allow toggling closed if we're NOT on an activity page
      setActivityDropdownOpen(!wasOpen);
    } else {
      // If we're on an activity page, allow toggling to reopen if it's closed
      setActivityDropdownOpen(!wasOpen);
    }
  };
  
  // Load analytics boards when in analytics or when user has pinned boards, so PINNED list and active highlight work on all pages
  useEffect(() => {
    if ((isInAnalytics || pinnedBoardIds.length > 0) && loadBoards) loadBoards();
  }, [isInAnalytics, pinnedBoardIds.length, loadBoards]);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Helper function to render navigation item with subscription-based access control
  const renderNavItem = (to, pageName, icon, label, alwaysAccessible = false) => {
    const isActiveItem = isActive(pageName);
    const hasAccess = alwaysAccessible || canAccessPage(pageName);
    
    // Define classes based on active state and access
    let itemClasses = isActiveItem 
      ? "text-[#0f172a] bg-gray-100"
      : "text-[#0f172a]";
    
    // Clone icon with appropriate color; thin stroke to match settings icon
    const iconColor = isActiveItem ? "black" : "black";
    const iconStroke = 0.8;
    const iconWithColor = React.cloneElement(icon, {
      style: { 
        color: iconColor,
        strokeWidth: iconStroke,
        ...icon.props.style 
      }
    });
    
    // If user doesn't have access, show as locked
    if (!hasAccess) {
      itemClasses += " opacity-50 cursor-not-allowed";
      
      // For locked items, use black color; thin stroke to match settings
      const lockedIcon = React.cloneElement(icon, {
        style: { 
          color: "black",
          strokeWidth: iconStroke,
          ...icon.props.style 
        }
      });
      
      return (
        <div 
          className={`${itemClasses} sidebar-nav-item flex items-center gap-2 ${!showExpanded ? "py-0.5 px-2 justify-center" : "py-1.5 px-3 mx-1 w-auto rounded-lg"}`}
          style={{ 
            fontFamily: 'Raleway, sans-serif'
          }}
          title={`Upgrade your plan to access ${label}`}
        >
          {lockedIcon}
          {showExpanded && (
            <div className="flex items-center justify-between w-full">
              <span className="transition-opacity duration-150 ease-out">{label}</span>
              <Lock size={14} strokeWidth={1} className="text-[#475569] ml-1" />
            </div>
          )}
          {!showExpanded && (
            <div className="absolute -right-1 -top-1">
              <Lock size={12} strokeWidth={1} className="text-[#475569]" />
            </div>
          )}
        </div>
      );
    }
    
    // User has access - render normal clickable nav item
    return (
      <Link 
        to={to}
        className={`${itemClasses} sidebar-nav-item flex items-center gap-2 ${isActiveItem ? "hover:bg-gray-100" : "hover:bg-gray-100"} ${!showExpanded ? "py-0.5 px-2 justify-center" : "py-1.5 px-3 mx-1 rounded-lg"} cursor-pointer`}
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          willChange: 'background-color, color',
          ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' })
        }}
        onClick={() => onNavigate && onNavigate(pageName)}
      >
        {iconWithColor}
        {showExpanded && (
          <div className="flex items-center justify-between w-full">
            <span className="transition-opacity duration-150 ease-out text-sm" style={{ willChange: 'opacity', color: '#0f172a' }}>{label}</span>
          </div>
        )}
      </Link>
    );
  };

  return (
    <aside
      style={{ 
        boxSizing: 'border-box', 
        paddingRight: 0, 
        marginRight: 0, 
        borderRightWidth: '1px',
        willChange: 'transform, width',
        backfaceVisibility: 'hidden',
        perspective: '1000px',
        borderRadius: '0px'
      }}
      className={`fixed md:relative glass-effect sidebar-optimized sidebar-transition ${!showExpanded ? "py-0.5 px-0.5" : "py-1 px-1"} flex flex-col h-screen border-r border-gray-200 transform ${currentPage === "settings" ? "z-10" : "z-50"} ${
        isOpen || forceVisible
          ? "translate-x-0 w-full sm:w-64 md:w-56 top-0 left-0 md:relative md:top-auto md:left-auto" 
          : "-translate-x-full md:translate-x-0 md:w-auto"
      } ${
        !showExpanded ? "md:w-[48px] pr-0" : "md:w-56 lg:w-64"
      }`}
    >
      {/* Team Selector */}
      {showExpanded && (
        <div className="relative mt-0.5 mb-0.5 mx-1" ref={dropdownRef}>
          <div className="relative w-full">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center space-x-2 px-4 py-1.5 text-sm whitespace-nowrap transition-all duration-200 hover:bg-gray-100 w-full rounded-lg ${
                dropdownOpen ? 'bg-gray-100' : 'bg-white'
              }`}
              style={{ fontFamily: 'Raleway, sans-serif' }}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-[#475569]">R</span>
                  </div>
                  <span className="truncate flex-1 text-[#475569]">Refreshing...</span>
                  <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse"></div>
                </>
              ) : (
                <>
                  <div 
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(to top right, #1d0c46, #4B57AA)' }}
                  >
                    <span className="text-xs font-medium text-white">{selectedTeam?.charAt(0)?.toUpperCase() || 'T'}</span>
                  </div>
                  <span className="truncate flex-1">{selectedTeam}</span>
                  <LuChevronsUpDown size={18} style={{ strokeWidth: 0.8 }} />
                </>
              )}
            </button>

              {dropdownOpen && (
                <div
                  className="absolute top-full left-0 right-0 mt-0.5 bg-white z-50 scrollbar-thin shadow-xl rounded-lg border border-gray-100"
                  style={{ maxHeight: '60vh', overflowY: 'auto' }}
                >
                  <div className="py-1.5">
                    {/* Projects Section */}
                    <div className="px-4 py-1">
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#666666', fontFamily: 'Raleway, sans-serif' }}>
                        Projects
                      </p>
                    </div>

                    {/* Teams List */}
                    {isLoadingTeams ? (
                      <div className="px-4 py-1.5 flex items-center justify-center">
                        <p className="text-sm text-center" style={{ color: '#0f172a', fontFamily: 'Raleway, sans-serif' }}>
                          Loading teams...
                        </p>
                      </div>
                    ) : curTeams.length > 0 ? (
                      (() => {
                        // Deduplicate teams by ID
                        const uniqueTeams = Array.from(
                          new Map(curTeams.map(team => [team.id || team._id, team])).values()
                        );
                        
                        return uniqueTeams.map((team) => {
                          const teamId = team.id || team._id;
                          if (!teamId) {
                            return null;
                          }
                          
                          const planDisplayName = getPlanDisplayName(team.subscription);
                          const badgeColor = getPlanBadgeColor(team.subscription);
                          const isSelected = team.name === selectedTeam;
                          
                          return (
                             <button
                               key={teamId}
                               onClick={() => handleTeamSelect(team)}
                               className={`w-full px-4 py-1.5 flex items-center justify-between text-sm transition-all duration-200 text-left ${
                                 isSelected 
                                   ? 'bg-gray-100' 
                                   : 'hover:bg-gray-100'
                               }`}
                               style={{ fontFamily: 'Raleway, sans-serif' }}
                             >
                               <span className="truncate" style={{ color: '#0f172a' }}>
                                 {team.name}
                               </span>
                               {planDisplayName && (
                                 <span className={`text-xs px-1.5 py-0.5 font-medium whitespace-nowrap flex-shrink-0 rounded ml-2 ${badgeColor}`}>
                                   {planDisplayName}
                                 </span>
                               )}
                             </button>
                          );
                        }).filter(Boolean);
                      })()
                    ) : (
                      <div className="px-4 py-1.5 flex items-center justify-center">
                        <p className="text-sm text-center" style={{ color: '#0f172a', fontFamily: 'Raleway, sans-serif' }}>
                          No teams available
                        </p>
                      </div>
                    )}

                    {/* New Project Button */}
                    <div className="border-t border-gray-200 my-1"></div>
                    <div className="px-2">
                      <button
                        onClick={handleCreateNewTeam}
                        disabled={isCreatingTeam}
                        className="flex items-center space-x-2 px-3 py-1.5 text-sm text-left hover:bg-gray-100 rounded transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Raleway, sans-serif', color: '#0f172a' }}
                      >
                        <span style={{ fontSize: '1.2rem', lineHeight: '1', fontWeight: 500 }}>+</span>
                        <span>{isCreatingTeam ? 'Creating...' : 'New Project'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
      
      <div className={`flex justify-between items-center ${!showExpanded ? "mb-1" : ""} ${!showExpanded ? "py-0.5" : "py-1"} ${!showExpanded ? "justify-center" : ""}`}>
        {showExpanded && <button className="md:hidden text-[#475569] hover:text-[#475569] p-2" onClick={() => { onClose(); hideMarketing && hideMarketing(); }}>✖</button>}
      </div>
      
      <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
        <ul className={`${!showExpanded ? "space-y-3 px-0 mt-2" : "space-y-1"} text-sm`} style={{ fontFamily: 'Raleway, sans-serif' }}>
          {/* Create New - popup dropdown (matching Mixpanel style) */}
          {showExpanded && (
            <div className="relative mb-2 mx-2" ref={createNewDropdownRef}>
              <button
                type="button"
                onClick={toggleCreateNewDropdown}
                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                style={{
                  color: "#fff",
                  backgroundColor: "#4B57AA",
                  fontFamily: "Raleway, sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#3d4699";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#4B57AA";
                }}
              >
                <span className="flex items-center gap-2">
                  <span className="text-lg leading-none">+</span>
                  Create New
                </span>
                <ChevronDown size={16} className={`flex-shrink-0 transition-transform ${createNewDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              <CreateNewDropdown
                open={createNewDropdownOpen}
                onClose={() => setCreateNewDropdownOpen(false)}
                anchorRef={createNewDropdownRef}
              />
            </div>
          )}
          
          {/* Home (Reports page) – first nav item */}
          {(() => {
            const isActiveItem = isReportsActive();
            return (
              <Link 
                to={`/${selectedTeam}/analytics/reports`}
                className={`${isActiveItem ? "text-black bg-gray-100" : "text-black"} sidebar-nav-item flex items-center gap-2 hover:bg-gray-100 ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1.5 px-3 mx-1 rounded-lg"} cursor-pointer`}
                style={{ 
                  fontFamily: 'Raleway, sans-serif',
                  willChange: 'background-color, color',
                  ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' })
                }}
              >
                <Icon name="sidebar_home" size={!showExpanded ? 20 : 18} color="black" />
                {showExpanded && (
                  <div className="flex items-center justify-between w-full">
                    <span className="transition-opacity duration-150 ease-out text-sm" style={{ willChange: 'opacity', color: 'black' }}>Home</span>
                  </div>
                )}
              </Link>
            );
          })()}
          
          {/* Overview with dropdown */}
          {(() => {
            const isActiveItem = isOverviewActive();
            const hasAccess = canAccessPage("overview");
            
            if (!hasAccess) {
              return (
                <div 
                  className="text-[#0f172a] opacity-50 cursor-not-allowed sidebar-nav-item flex items-center gap-2 py-1.5 px-3 w-full"
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                  title="Upgrade your plan to access Overview"
                >
                  <Icon name="sidebar_overview" size={!showExpanded ? 20 : 18} color="black" />
                  {showExpanded && (
                    <div className="flex items-center justify-between w-full">
                      <span className="transition-opacity duration-150 ease-out">Overview</span>
                      <Lock size={14} strokeWidth={1} className="text-[#475569] ml-1" />
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <div className="w-full" ref={overviewDropdownRef}>
                <button
                  onClick={toggleOverviewDropdown}
                  className={`${isActiveItem ? "text-[#0f172a] bg-gray-100" : "text-[#0f172a]"} sidebar-nav-item flex items-center gap-2 hover:bg-gray-100 ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1.5 px-3 mx-1 rounded-lg"} cursor-pointer`}
                  style={{ 
                    fontFamily: 'Raleway, sans-serif',
                    willChange: 'background-color, color',
                    ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' })
                  }}
                >
                  <Icon name="sidebar_overview" size={!showExpanded ? 20 : 18} color="black" />
                  {showExpanded && (
                    <div className="flex items-center justify-between w-full">
                      <span className="transition-opacity duration-150 ease-out">Overview</span>
                      <ChevronDown size={16} strokeWidth={1} className={`transition-transform ${overviewDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                  )}
                </button>
                
                {overviewDropdownOpen && showExpanded && (
                  <div className="w-full">
                    <Link
                      to={`/${selectedTeam}/overview/hub`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/overview/hub') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Hub</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/overview/source`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/overview/source') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color',
                        width: 'calc(100% - 1rem)'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Source</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/overview/product-usage`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/overview/product-usage') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Product Usage</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/overview/onchain`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/overview/onchain') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Onchain</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/overview/retention`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/overview/retention') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Retention</span>
                    </Link>
                    {/* Temporarily hidden - Wallet List */}
                    {/* <Link
                      to={`/${selectedTeam}/overview/wallet-list`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/overview/wallet-list') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Wallet list</span>
                    </Link> */}
                  </div>
                )}
              </div>
            );
          })()}
            
          {/* Activity with dropdown */}
          {(() => {
            const isActiveItem = isActivityActive();
            const hasAccess = canAccessPage("activity");
            
            if (!hasAccess) {
              return (
                <div 
                  className="text-[#0f172a] opacity-50 cursor-not-allowed sidebar-nav-item flex items-center gap-2 py-1.5 px-3 w-full"
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                  title="Upgrade your plan to access Activity"
                >
                  <Icon name="sidebar_activity" size={!showExpanded ? 20 : 18} color="black" />
                  {showExpanded && (
                    <div className="flex items-center justify-between w-full">
                      <span className="transition-opacity duration-150 ease-out">Activity</span>
                      <Lock size={14} strokeWidth={1} className="text-[#475569] ml-1" />
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <div className="w-full" ref={activityDropdownRef}>
                <button
                  onClick={toggleActivityDropdown}
                  className={`${isActiveItem ? "text-[#0f172a] bg-gray-100" : "text-[#0f172a]"} sidebar-nav-item flex items-center gap-2 hover:bg-gray-100 ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1.5 px-3 mx-1 rounded-lg"} cursor-pointer`}
                  style={{ 
                    fontFamily: 'Raleway, sans-serif',
                    willChange: 'background-color, color',
                    ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' })
                  }}
                >
                  <Icon name="sidebar_activity" size={!showExpanded ? 20 : 18} color="black" />
                  {showExpanded && (
                    <div className="flex items-center justify-between w-full">
                      <span className="transition-opacity duration-150 ease-out">Activity</span>
                      <ChevronDown size={16} strokeWidth={1} className={`transition-transform ${activityDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                  )}
                </button>
                
                {activityDropdownOpen && showExpanded && (
                  <div className="w-full">
                    <Link
                      to={`/${selectedTeam}/activity/offchain`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/activity/offchain') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Sessions</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/activity/events`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/activity/events') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Events</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/activity/onchain`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/activity/onchain') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Transactions</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/activity/users`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/activity/users') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Users</span>
                    </Link>
                    <Link
                      to={`/${selectedTeam}/activity/heatmap`}
                      className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 cursor-pointer pl-12 rounded-lg ${
                        location.pathname.includes('/activity/heatmap') 
                          ? 'text-[#0f172a] bg-gray-100 hover:bg-gray-100' 
                          : 'text-[#0f172a] hover:bg-gray-100'
                      }`}
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        willChange: 'background-color, color'
                      }}
                    >
                      <span className="transition-opacity duration-150 ease-out">Heatmap</span>
                    </Link>
                  </div>
                )}
              </div>
            );
          })()}
            
          {/* User Profiles page disabled */}
          {/* {renderNavItem(`/${selectedTeam}/user-journey`, "history", 
            <MdContactPage size={!showExpanded ? 20 : 18} />, 
            "User Profiles")} */}
          
          
          {/* Custom Events page hidden from sidebar */}
          {/* {renderNavItem(`/${selectedTeam}/custom-events`, "custom-events", 
            <BsCalendarEventFill size={!showExpanded ? 20 : 18} />, 
            "Custom Events")} */}
            
          {/* Campaigns moved to Console page */}
          {/* {renderNavItem(`/${selectedTeam}/campaigns`, "campaigns", 
            <CiBullhorn size={!showExpanded ? 20 : 18} />, 
            "Campaigns")} */}
            
          {/* CQ Intelligence hidden from navbar for now */}
          {/* {renderNavItem(`/${selectedTeam}/cq-intelligence`, "cq-intelligence", 
            <CiSatellite1 size={!showExpanded ? 20 : 18} />, 
            "CQ Intelligence")} */}
            
          {renderNavItem(`/${selectedTeam}/cipher`, "cipher", 
            <Icon name="sidebar_console" size={!showExpanded ? 20 : 18} color="black" />, 
            "Console", true)}
            
          {/* Pinned Section – always visible when sidebar expanded; only boards user has pinned */}
          {showExpanded && (
            <div className="mt-4 mb-2">
              <div className="px-3 py-2 mb-1">
                <div className="flex items-center gap-2">
                  <Icon name="sidebar_pinned" size={14} color="#64748b" />
                  <span 
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ 
                      color: "#64748b",
                      fontFamily: "Raleway, sans-serif"
                    }}
                  >
                    PINNED
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                {pinnedBoards.map((board) => {
                  const isActiveBoard = isInAnalyticsBoards && currentBoardIdFromPath === board.id;
                  return (
                  <Link
                    key={board.id}
                    to={`/${selectedTeam}/analytics/boards/${board.id}`}
                    className={`sidebar-nav-item flex items-center gap-2 py-1.5 px-3 mx-1 rounded-lg cursor-pointer ${
                      isActiveBoard
                        ? "text-black bg-gray-100 hover:bg-gray-100"
                        : "text-black hover:bg-gray-100"
                    }`}
                    style={{ 
                      fontFamily: "Raleway, sans-serif",
                      willChange: "background-color, color",
                      width: "calc(100% - 1rem)"
                    }}
                  >
                    <Icon name="dashboard" size={18} color="black" style={{ flexShrink: 0 }} />
                    <span className="text-sm flex-1 truncate">{board.name}</span>
                  </Link>
                  );
                })}
              </div>
            </div>
          )}
          
        </ul>
      </nav>
      <div className="border-t pt-1 mt-1">
        {/* Bottom icon row: Support + Settings + Collapse - stacked vertically when collapsed */}
        <div className={`relative flex items-center gap-1 py-1 px-1 ${showExpanded ? 'flex-row justify-end' : 'flex-col justify-center'}`} ref={supportPopupRef}>
          {/* Both popups positioned relative to icon row - same placement */}
          {(supportPopupOpen || settingsPopupOpen) && (
            <div className={`absolute z-50 bottom-full mb-1 ${showExpanded ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}>
              {supportPopupOpen && (
                <div className="bg-white border border-gray-200 rounded-[12px] shadow-xl overflow-hidden min-w-[200px]">
                  <div className="py-1.5" style={{ fontFamily: 'Raleway, sans-serif' }}>
                    <a href="https://docs.cryptique.io/" target="_blank" rel="noopener noreferrer" onClick={() => setSupportPopupOpen(false)} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a', textDecoration: 'none' }}>
                      <Icon name="sidebar_docs" size={18} color="#0f172a" /> Help Docs
                    </a>
                    <a href="https://docs.cryptique.io/changelog/overview" target="_blank" rel="noopener noreferrer" onClick={() => setSupportPopupOpen(false)} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a', textDecoration: 'none' }}>
                      <Icon name="sidebar_changelog" size={18} color="#0f172a" /> Changelog
                    </a>
                    <button onClick={() => { setSupportPopupOpen(false); openTawkSupport(); }} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a' }}>
                      <Icon name="sidebar_support" size={18} color="#0f172a" /> Contact Support
                    </button>
                    <a href="mailto:sales@cryptique.io" onClick={() => setSupportPopupOpen(false)} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a', textDecoration: 'none' }}>
                      <Icon name="sidebar_contactsales" size={18} color="#0f172a" /> Contact Sales
                    </a>
                  </div>
                </div>
              )}
              {settingsPopupOpen && (
                <div className="bg-white border border-gray-200 rounded-[12px] shadow-xl overflow-hidden min-w-[200px]">
                  <div className="py-1.5" style={{ fontFamily: 'Raleway, sans-serif' }}>
                    <button onClick={() => { navigate(`/${selectedTeam}/settings`); setSettingsPopupOpen(false); }} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a' }}>
                      <Icon name="sidebar_settings" size={18} color="#0f172a" /> Project Settings
                    </button>
                    <button onClick={() => { navigate(`/${selectedTeam}/user-settings`); setSettingsPopupOpen(false); }} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a' }}>
                      <Icon name="sidebar_usersettings" size={18} color="#0f172a" /> User settings
                    </button>
                    <button onClick={() => { navigate(`/${selectedTeam}/managewebsites`); setSettingsPopupOpen(false); }} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a' }}>
                      <Icon name="sidebar_apps" size={18} color="#0f172a" /> Apps
                    </button>
                    <button onClick={() => { navigate(`/${selectedTeam}/add-smart-contract`); setSettingsPopupOpen(false); }} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a' }}>
                      <Icon name="sidebar_smartcontract" size={18} color="#0f172a" /> Smart Contract
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <div className="px-4 py-2 text-sm" style={{ color: '#0f172a' }}>{userName}</div>
                    <button onClick={(e) => { handleLogout(e); setSettingsPopupOpen(false); }} className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors" style={{ color: '#0f172a' }}>
                      <Icon name="sidebar_logout" size={18} color="#0f172a" /> Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {notificationsPopupOpen && notificationsPopupLayout && typeof document !== 'undefined' ? createPortal(
            <div ref={notificationsPopupRef} style={notificationsPopupLayout.wrapper}>
              <NotificationsPopup
                isOpen={notificationsPopupOpen}
                unreadCount={unreadNotifications}
                onUnreadCountChange={setUnreadNotifications}
                onClose={() => setNotificationsPopupOpen(false)}
                containerStyle={notificationsPopupLayout.containerStyle}
              />
            </div>,
            document.body
          ) : null}
          <button
            onClick={() => { setSupportPopupOpen(!supportPopupOpen); setSettingsPopupOpen(false); setNotificationsPopupOpen(false); }}
            className="flex items-center justify-center px-3 py-1.5 rounded hover:bg-gray-100 transition-colors text-[#0f172a] hover:text-[#0f172a]"
            title="Support"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            <Icon name="sidebar_supporticon" size={22} color="#0f172a" />
          </button>
          <button
            onClick={() => {
              setSupportPopupOpen(false);
              setSettingsPopupOpen(false);
              setNotificationsPopupOpen((v) => !v);
            }}
            ref={notificationsBellButtonRef}
            className="relative flex items-center justify-center px-3 py-1.5 rounded hover:bg-gray-100 transition-colors text-[#0f172a] hover:text-[#0f172a]"
            title="Notifications"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            <Bell size={20} strokeWidth={1.6} />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#4B57AA] text-white text-[10px] leading-4 text-center">
                {unreadNotifications > 99 ? '99+' : unreadNotifications}
              </span>
            )}
          </button>
          <div ref={settingsPopupRef}>
            <button
              onClick={() => { setSettingsPopupOpen(!settingsPopupOpen); setSupportPopupOpen(false); setNotificationsPopupOpen(false); }}
              className={`flex items-center justify-center px-3 py-1.5 rounded hover:bg-gray-100 transition-colors text-[#0f172a] hover:text-[#0f172a] ${pathCurrentPage === 'settings' ? 'bg-gray-100' : ''}`}
              title="Settings"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              <Icon name="sidebar_settings" size={22} color="#0f172a" />
            </button>
          </div>
          <button
            className="hidden md:flex items-center justify-center px-3 py-1.5 text-[#475569] hover:text-[#475569] rounded hover:bg-gray-100 transition-colors"
            onClick={toggleCollapse}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            <Icon name="sidebar_collapsesidebar" size={22} color="#475569" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;